// src/components/CategorySidebar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useChatStore } from "../store/useChatStore";

const colors = {
  bg: "#ffffff",
  border: "#e8e8ef",
  text: "#111827",
  subtext: "#6b7280",
  brand: "#444CE7",
  brandSoft: "rgba(68,76,231,0.08)",
  hover: "#f7f8ff",
  badge: "#ef4444",
};

const canon = (s) =>
  String(s || "")
    .trim()
    .toUpperCase();

const PALETTE = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#059669",
  "#0ea5e9",
  "#ea580c",
  "#16a34a",
  "#9333ea",
  "#d97706",
  "#ef4444",
];
const colorFor = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
};

export default function CategorySidebar({ onSelect }) {
  const {
    categories,
    categoryUnreadMap,
    selectedCategory,
    getCategoryList,
    setSelectedCategory,
  } = useChatStore();

  const [q, setQ] = useState("");
  const [hovered, setHovered] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    if (typeof getCategoryList === "function") getCategoryList();
  }, [getCategoryList]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    return (categories || []).filter((c) =>
      String(c.name || c.category)
        .toLowerCase()
        .includes(k),
    );
  }, [q, categories]);

  return (
    <aside
      style={{
        height: "100%",
        background: colors.bg,
        borderRight: `1px solid ${colors.border}`,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        width: isMobile ? "100%" : "auto",
        maxHeight: "100%",
      }}
    >
      {/* ---------- Header (Sticky) ---------- */}
      <div
        style={{
          position: "sticky",
          top: 0,
          background: colors.bg,
          borderBottom: `1px solid ${colors.border}`,
          zIndex: 5,
          padding: isMobile ? "10px 14px" : "12px 16px",
        }}
      >
        <div
          style={{
            fontWeight: 800,
            fontSize: isMobile ? 16 : 17,
            marginBottom: 10,
            color: colors.text,
          }}
        >
          Categories
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search..."
            style={{
              flex: 1,
              padding: isMobile ? "7px 10px" : "8px 12px",
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              fontSize: isMobile ? 14 : 15,
            }}
          />
          <button
            onClick={() => setQ("")}
            style={{
              border: `1px solid ${colors.border}`,
              background: "#fff",
              borderRadius: 8,
              padding: isMobile ? "6px 10px" : "8px 12px",
              cursor: "pointer",
              fontSize: isMobile ? 14 : 15,
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* ---------- Category List ---------- */}
      <div style={{ paddingBottom: 10 }}>
        {filtered.length === 0 && (
          <div
            style={{
              color: colors.subtext,
              padding: isMobile ? 14 : 18,
              fontSize: isMobile ? 14 : 15,
            }}
          >
            No categories found.
          </div>
        )}

        {filtered.map((row) => {
          const cat = String(row.category || "").trim();
          const label = row.name || cat;
          const active = selectedCategory === cat;
          const unread = categoryUnreadMap?.[canon(cat)] ?? row.unread ?? 0;
          const iconBg = colorFor(cat);

          return (
            <div
              key={cat}
              onClick={() => {
                setSelectedCategory(cat);
                onSelect?.(cat);
              }}
              onMouseEnter={() => setHovered(cat)}
              onMouseLeave={() => setHovered(null)}
              style={{
                display: "grid",
                gridTemplateColumns: "28px 1fr auto",
                alignItems: "center",
                gap: 12,
                padding: isMobile ? "10px 14px" : "14px 18px",
                cursor: "pointer",
                userSelect: "none",
                background: active
                  ? colors.brandSoft
                  : hovered === cat
                    ? colors.hover
                    : "transparent",
                position: "relative",
                transition: "background 0.15s ease",
              }}
            >
              {/* Left border indicator */}
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  top: 8,
                  bottom: 8,
                  width: 4,
                  borderRadius: 999,
                  background: active ? colors.brand : "transparent",
                }}
              />

              {/* Icon */}
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: iconBg,
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: isMobile ? 13 : 14,
                }}
              >
                {label?.[0]?.toUpperCase?.() || "•"}
              </div>

              {/* Category Name */}
              <div
                style={{
                  fontWeight: 800,
                  fontSize: isMobile ? 14.5 : 15.5,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: colors.text,
                }}
              >
                {label}
              </div>

              {/* Unread Badge */}
              {!!unread && (
                <span
                  style={{
                    background: colors.badge,
                    color: "#fff",
                    borderRadius: 999,
                    padding: isMobile ? "1px 6px" : "2px 8px",
                    fontSize: isMobile ? 11.5 : 12,
                    fontWeight: 700,
                  }}
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
